import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Users, CheckCircle, XCircle } from 'lucide-react';

interface CreationResult {
  message: string;
  created: string[];
  errors: { email: string; error: string }[];
  total_created: number;
  total_errors: number;
}

const ManageTeam = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<CreationResult | null>(null);
  const { toast } = useToast();

  const handleCreateAccounts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-team-accounts');

      if (error) throw error;

      setResults(data);
      
      toast({
        title: "Team Accounts Created",
        description: `Successfully created ${data.total_created} accounts${data.total_errors > 0 ? ` with ${data.total_errors} errors` : ''}`,
        variant: data.total_errors > 0 ? "destructive" : "default"
      });
    } catch (error: any) {
      console.error('Error creating team accounts:', error);
      toast({
        title: "Creation Failed",
        description: error.message || "Failed to create team accounts",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-6 w-6" />
              Team Account Management
            </CardTitle>
            <CardDescription>
              Create and manage team member accounts for the iSPAI channel
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Button 
                onClick={handleCreateAccounts} 
                disabled={loading}
                size="lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Accounts...
                  </>
                ) : (
                  'Create Team Accounts'
                )}
              </Button>
              <p className="text-sm text-muted-foreground mt-2">
                This will create accounts for 12 team members with initial password "password123"
              </p>
            </div>

            {results && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Successfully Created
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{results.total_created}</div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <XCircle className="h-4 w-4 text-destructive" />
                        Errors
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{results.total_errors}</div>
                    </CardContent>
                  </Card>
                </div>

                {results.created.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Created Accounts
                    </h3>
                    <ul className="space-y-1">
                      {results.created.map((email) => (
                        <li key={email} className="text-sm text-muted-foreground">
                          {email}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {results.errors.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2 flex items-center gap-2 text-destructive">
                      <XCircle className="h-4 w-4" />
                      Errors
                    </h3>
                    <ul className="space-y-2">
                      {results.errors.map((err) => (
                        <li key={err.email} className="text-sm">
                          <span className="font-medium">{err.email}:</span>{' '}
                          <span className="text-destructive">{err.error}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ManageTeam;
